import { useState } from 'react';

import { Box, Grid, Typography } from '@mui/material';

import {
	BlogList as BlogListDetails,
	BlogListPageDescription,
	Brand,
	ContentTypeRichTextContentLinks,
	BlogListPageContentListCollection,
	BlogListPageContentAfterListCollection,
} from '@/types/generated';
import RichText, { RichTextContent } from '@/components/RichText';

import { ICardDesignType } from '../BlogList/BlogListItem';
import BlogList from '../BlogList/BlogList';

import { getTitleContainerStyles, titleStyles } from './BlogListPage.styles';

interface IBlogListDetails extends BlogListDetails {
	cardDesignType?: ICardDesignType;
}
export interface IBlogListPage {
	blogList: IBlogListDetails;
	brand: Brand;
	title?: string;
	description?: BlogListPageDescription;
	preview?: boolean;
	contentListCollection?: BlogListPageContentListCollection;
	contentAfterListCollection?: BlogListPageContentAfterListCollection;
	shouldDisplayCategoryFilterChips?: boolean;
}

export interface IBlogContentListItem extends BlogListDetails {
	startIndexBlogList: number;
	setStartIndexBlogList: (startIndex: number) => void;
}

type TCategoryChipType = 'category' | 'subCategory' | 'all';

interface ICategoryChip {
	name: string;
	type: TCategoryChipType;
}

const allBlogsCategoryChip: ICategoryChip = {
	name: 'All Blogs',
	type: 'all',
};

export default function BlogListPageVariant({
	blogList,
	title,
	description,
	preview = false,
}: Readonly<IBlogListPage>) {
	const [categoriesData] = useState<ICategoryChip[]>([]);
	const [selectedCategory] = useState<ICategoryChip | null>(allBlogsCategoryChip);
	const [startIndexBlogList, setStartIndexBlogList] = useState(0);

	const shouldRenderContainer = !!title || !!description || !!categoriesData.length;

	return (
		<>
			{shouldRenderContainer && (
				<Grid container sx={getTitleContainerStyles}>
					{title && (
						<Typography
							component="h1"
							variant="heroMedium"
							color="text.primary"
							data-test-id="blog_list_page_title"
							sx={titleStyles}
						>
							{title}
						</Typography>
					)}
					{description && (
						<Box
							data-test-id={'blog_list_page_description'}
							sx={{
								width: '100%',
								marginBottom: { xs: '40px', md: '0' },
							}}
						>
							<RichText
								links={description?.links as ContentTypeRichTextContentLinks}
								content={description?.json as RichTextContent}
								docProps={{
									color: 'text.light',
									variant: 'bodyXLBook',
									fontSize: '24px',
									fontFamily: 'Messina Modern',
									letterSpacing: '-0.5px',
								}}
							/>
						</Box>
					)}
				</Grid>
			)}
			<BlogList
				{...blogList}
				preview={preview}
				startIndexBlogList={startIndexBlogList}
				setStartIndexBlogList={setStartIndexBlogList}
				categoryName={selectedCategory?.type === 'category' ? selectedCategory.name : undefined}
				subCategoryName={selectedCategory?.type === 'subCategory' ? selectedCategory.name : undefined}
			/>
		</>
	);
}
